import React from 'react';
import { Link } from 'react-router-dom';
import { useEntitlement } from '../../data/useEntitlement';
import { useDateFormat, useLocale } from '../../context/LocaleContext';
import { STATES } from '../../domain/billingPlans';
import { priceTerms } from '../../domain/priceDisclosure';
import { BRAND } from '../../domain/brand';
import s from './billing.module.css';

// How long this studio has got, and what happens when it runs out.
//
// ── Which states show it ────────────────────────────────────────────────────
//
// `trial` and `grace`, and nothing else: the two states with a clock running
// that the teacher can do something about. Not `active` or `comped` — a studio
// that is paying, or has been given the app for nothing, has no decision to
// make, and a strip above every page charged to them is how a banner becomes
// something people stop reading. Not `granted`, by the same test: awarded
// months are a gift. Not `lapsed`: a strip is the wrong size for that news,
// which is the paywall's (Paywall.jsx).
//
// ── The numbers are the verdict's ───────────────────────────────────────────
//
// `daysLeft` and `accessUntil` come from `entitlementAsOf`, with no arithmetic
// here: a banner counting its own days would be a second answer to "how long
// have I got", and could disagree with the paywall by one. `accessUntil` is
// the furthest any source of access reaches rather than the reported state's
// own end date, which matters for a paying studio with an awarded month on top
// (the note above `accessUntil` in domain/entitlement.js).
//
// Not an MMS parity element: the copy and placement are ours.

export default function TrialBanner() {
  const {
    state, daysLeft, accessUntil, priceMinor, verdict, loading,
  } = useEntitlement();
  const formatDate = useDateFormat();
  // The studio's locale for the number, the billing currency for the money:
  // the currency is BILLING_CURRENCY (USD), which `priceTerms` formats in
  // (domain/priceDisclosure.js). A studio in Seoul invoices its families in won
  // and pays us in dollars, and domain/locale.js answers only the first;
  // the studio's currency here would print "₩6.99".
  const { locale } = useLocale();

  // Nothing until the record has actually been read. A banner drawn from an
  // unread account would be announcing a trial that may not exist.
  if (loading) return null;
  if (state !== STATES.trial && state !== STATES.grace) return null;

  // What they will pay: "$6.99 a month", the one price (#722), in
  // domain/priceDisclosure.js's words.
  const price = priceTerms({ priceMinor, locale });

  if (state === STATES.trial) {
    // `daysLeft` is inclusive of today, so 0 is the last day rather than a
    // trial that has already ended — a trial that had ended would not be in
    // this state at all.
    const headline = daysLeft === 0
      ? 'Your free trial ends today'
      : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your free trial`;

    return (
      <div className={s.banner} role="status">
        <span className={s.headline}>{headline}</span>
        <span className={s.detail}>
          {/* What happens next, in the words it will happen in: the price is
              the verdict's, $6.99 for every studio that is not comped (#722).

              Then what the link beside it does to that (#713): checkout starts
              the subscription on a trial that ends with this one
              (functions/billing/callables.js `checkoutTrialEnd`), so a card
              added now is not charged before the trial ends; in the last two
              days Stripe's 48-hour floor makes the first charge later still,
              never earlier. The date is the verdict's, as every date here
              is. */}
          After that, {BRAND.name} is {price}. A card added now isn&rsquo;t charged
          until your trial ends on {formatDate(verdict.trialEndsOn)}.
        </span>
        <span className={s.spacer} />
        <Link className={s.action} to="/settings#membership">Add a payment method</Link>
      </div>
    );
  }

  // grace: the card failed and Stripe is still retrying it. Deliberately not
  // phrased as a lockout — most of these retries succeed, and the studio has
  // usually done nothing wrong beyond letting a card expire.
  return (
    <div className={s.banner} role="status">
      <span className={s.headline}>We couldn&rsquo;t take your last payment</span>
      <span className={s.detail}>
        We&rsquo;ll keep trying your card
        {accessUntil ? `, and your studio stays open until ${formatDate(accessUntil)}` : ''}.
      </span>
      <span className={s.spacer} />
      <Link className={s.action} to="/settings#membership">Update payment method</Link>
    </div>
  );
}
